import React from "react";
import { motion, AnimatePresence } from "framer-motion";

export const slideVariants = {
  enter:  { opacity: 0, x: 40, scale: 0.97 },
  center: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.38, ease: [0.16, 1, 0.3, 1] } },
  exit:   { opacity: 0, x: -40, scale: 0.97, transition: { duration: 0.25, ease: "easeIn" } },
};

/**
 * MobileStep — Step 1: enter mobile number
 * Props: mobile, setMobile, mobileError, onSendOTP
 */
const MobileStep = ({ mobile, setMobile, mobileError, onSendOTP, sendingOTP }) => (
  <motion.div
    key="mobile"
    variants={slideVariants}
    initial="enter"
    animate="center"
    exit="exit"
    className="flex flex-col"
  >
    {/* Icon + heading */}
    <div className="flex flex-col items-center mb-5">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 shadow-md bg-[#00713b]"
      >
        <motion.svg
          className="w-7 h-7 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </motion.svg>
      </div>
      <h1 className="text-2xl font-semibold text-gray-700 mb-1 w-full text-center tracking-tight">Welcome!</h1>
      <p className="text-xs text-gray-500 font-medium text-center">
        Enter your mobile number to get started
      </p>
    </div>

    {/* Mobile number input */}
    <div className="flex flex-col mb-5">
      <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2 px-1">
        Mobile Number
      </label>
      <div
        className={`flex items-center border rounded-2xl px-4 py-3.5 bg-white transition-all duration-200 ${
          mobileError
            ? "border-rose-400 bg-rose-50/30"
            : "border-[#22c55e]/30 focus-within:border-emerald-600 focus-within:ring-2 focus-within:ring-emerald-500/15"
        }`}
      >
        <span className="text-sm font-bold text-gray-700 mr-3 border-r border-gray-200 pr-3 select-none">
          +91
        </span>
        <input
          id="login-mobile-input"
          type="tel"
          maxLength="10"
          placeholder="Enter 10-digit number"
          value={mobile}
          onChange={(e) => {
            const inputValue = e.target.value;
            if (!inputValue || /^\d*$/.test(inputValue)) setMobile(inputValue);
          }}
          onKeyDown={(e) => { if (e.key === "Enter") onSendOTP(); }}
          className="flex-1 bg-transparent border-none outline-none text-base text-gray-900 placeholder-gray-400 font-semibold tracking-wide"
        />
      </div>
      <AnimatePresence>
        {mobileError && (
          <motion.span
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-[11px] text-rose-500 font-bold mt-1.5 px-1"
          >
            {mobileError}
          </motion.span>
        )}
      </AnimatePresence>
    </div>

    {/* Send OTP button */}
    <motion.button
      id="login-send-otp-btn"
      onClick={onSendOTP}
      disabled={sendingOTP}
      whileHover={sendingOTP ? {} : { scale: 1.01 }}
      whileTap={sendingOTP ? {} : { scale: 0.97 }}
      className="w-full py-3.5 text-white font-semibold rounded-2xl text-base shadow-lg flex items-center justify-center gap-2.5 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
      style={{ background: "linear-gradient(90deg, #00713b 0%, #04b26b 100%)" }}
    >
      {sendingOTP ? (
        <>
          <svg className="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Sending OTP…
        </>
      ) : (
        "Send OTP"
      )}
    </motion.button>
  </motion.div>
);

export default MobileStep;
